import { useEffect, useRef } from "react";
import { X, Keyboard } from "lucide-react";

interface KeyboardShortcutsDialogProps {
    open: boolean;
    onClose: () => void;
}

const SHORTCUT_GROUPS = [
    {
        title: "Navigation",
        shortcuts: [
            { keys: ["⌘", "1"], description: "Dashboard" },
            { keys: ["⌘", "2"], description: "Kalender" },
            { keys: ["⌘", "3"], description: "Postfach" },
            { keys: ["⌘", "4"], description: "Tickets" },
            { keys: ["⌘", "5"], description: "KI-Telefon" },
            { keys: ["⌘", "6"], description: "Patienten" },
            { keys: ["⌘", "7"], description: "Abrechnung" },
            { keys: ["⌘", "9"], description: "Einstellungen" },
        ],
    },
    {
        title: "Aktionen",
        shortcuts: [
            { keys: ["⌘", "K"], description: "Befehlspalette öffnen" },
            { keys: ["⌘", "⇧", "K"], description: "Tastaturkürzel anzeigen" },
            { keys: ["Esc"], description: "Dialog / Panel schließen" },
        ],
    },
    {
        title: "In Listen & Tabellen",
        shortcuts: [
            { keys: ["↑", "↓"], description: "Eintrag auswählen" },
            { keys: ["Enter"], description: "Eintrag öffnen / bestätigen" },
            { keys: ["Tab"], description: "Nächstes Element" },
        ],
    },
] as const;

export function KeyboardShortcutsDialog({ open, onClose }: KeyboardShortcutsDialogProps) {
    const dialogRef = useRef<HTMLDivElement>(null);
    const closeRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (!open) return;

        // Focus close button on open
        requestAnimationFrame(() => closeRef.current?.focus());

        function handleKeyDown(e: KeyboardEvent) {
            if (e.key === "Escape") onClose();
        }
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [open, onClose]);

    if (!open) return null;

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 z-[60] bg-neutral-900/40 backdrop-blur-sm animate-fade-in"
                onClick={onClose}
                aria-hidden="true"
            />

            {/* Dialog */}
            <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
                <div
                    ref={dialogRef}
                    className="w-full max-w-lg rounded-xl border border-neutral-200 dark:border-neutral-600 bg-white dark:bg-neutral-800 shadow-drawer animate-slide-in"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Tastaturkürzel"
                >
                    {/* Header */}
                    <div className="flex items-center justify-between border-b border-neutral-200 dark:border-neutral-600 px-6 py-4">
                        <div className="flex items-center gap-2">
                            <Keyboard className="h-5 w-5 text-brand-500" />
                            <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
                                Tastaturkürzel
                            </h2>
                        </div>
                        <button
                            ref={closeRef}
                            type="button"
                            onClick={onClose}
                            className="rounded-md p-1 text-neutral-400 dark:text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-700 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                            aria-label="Schließen"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="max-h-[60vh] overflow-y-auto px-6 py-4 space-y-6">
                        {SHORTCUT_GROUPS.map((group) => (
                            <div key={group.title}>
                                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-neutral-400 dark:text-neutral-500">
                                    {group.title}
                                </h3>
                                <div className="space-y-1.5">
                                    {group.shortcuts.map((shortcut) => (
                                        <div
                                            key={shortcut.description}
                                            className="flex items-center justify-between rounded-md px-2 py-1.5 hover:bg-neutral-50 dark:hover:bg-neutral-700"
                                        >
                                            <span className="text-sm text-neutral-700 dark:text-neutral-300">
                                                {shortcut.description}
                                            </span>
                                            <div className="flex items-center gap-1">
                                                {shortcut.keys.map((key) => (
                                                    <kbd
                                                        key={key}
                                                        className="rounded border border-neutral-200 dark:border-neutral-600 bg-neutral-50 dark:bg-neutral-800 px-1.5 py-0.5 text-xs font-medium text-neutral-500 dark:text-neutral-400 min-w-[1.5rem] text-center"
                                                    >
                                                        {key}
                                                    </kbd>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Footer */}
                    <div className="border-t border-neutral-100 dark:border-neutral-700 px-6 py-3">
                        <p className="text-xs text-neutral-400 dark:text-neutral-500 text-center">
                            Auf Windows/Linux: ⌘ = Strg
                        </p>
                    </div>
                </div>
            </div>
        </>
    );
}
